/**
 * Typed fetch wrapper for TradeDesk API.
 * All requests are relative to /api — works both in dev (Vite proxy → :3000)
 * and in production (same-origin serving).
 *
 * Reads JWT from localStorage key 'td_token' and attaches Bearer header.
 * On 401: removes token and redirects to app root (login page).
 */

const TOKEN_KEY = "td_token";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOptions {
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options?: RequestOptions,
): Promise<T> {
  const url = path.startsWith("/api") ? path : `/api${path}`;

  const storedToken = localStorage.getItem(TOKEN_KEY);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...options?.headers,
  };
  if (storedToken != null) {
    headers["Authorization"] = `Bearer ${storedToken}`;
  }

  const fetchInit: RequestInit = {
    method,
    headers,
  };
  if (body != null) {
    fetchInit.body = JSON.stringify(body);
  }
  if (options?.signal != null) {
    fetchInit.signal = options.signal;
  }

  const res = await fetch(url, fetchInit);

  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.dispatchEvent(new CustomEvent("td:unauthorized"));
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { message?: string; error?: string; detail?: string };
      message = json.detail ?? json.message ?? json.error ?? message;
    } catch {
      // ignore parse error
    }
    throw new ApiError(res.status, message);
  }

  // 204 No Content — return empty object cast to T
  if (res.status === 204) {
    return {} as T;
  }

  return res.json() as Promise<T>;
}

async function requestForm<T>(
  path: string,
  formData: FormData,
  options?: RequestOptions,
): Promise<T> {
  const url = path.startsWith("/api") ? path : `/api${path}`;

  const storedToken = localStorage.getItem(TOKEN_KEY);

  const headers: Record<string, string> = { ...options?.headers };
  if (storedToken != null) {
    headers["Authorization"] = `Bearer ${storedToken}`;
  }

  const fetchInit: RequestInit = {
    method: "POST",
    headers,
    body: formData,
  };
  if (options?.signal != null) {
    fetchInit.signal = options.signal;
  }

  const res = await fetch(url, fetchInit);

  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.dispatchEvent(new CustomEvent("td:unauthorized"));
    throw new ApiError(401, "Unauthorized");
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const json = (await res.json()) as { message?: string; error?: string; detail?: string };
      message = json.detail ?? json.message ?? json.error ?? message;
    } catch {
      // ignore parse error
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) {
    return {} as T;
  }

  return res.json() as Promise<T>;
}

export const api = {
  get<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>("GET", path, undefined, options);
  },

  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>("POST", path, body, options);
  },

  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>("PUT", path, body, options);
  },

  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>("PATCH", path, body, options);
  },

  del<T>(path: string, options?: RequestOptions): Promise<T> {
    return request<T>("DELETE", path, undefined, options);
  },

  postForm<T>(path: string, formData: FormData, options?: RequestOptions): Promise<T> {
    return requestForm<T>(path, formData, options);
  },
};
